"use client"

import {
    type ModalState,
    type OutOfStockItem
} from "@/app/(site)/[locale]/checkout/types"
import { IconClose } from "@/app/(site)/_components/Atoms/Icons"
import Modal from "@/app/(site)/_components/Atoms/Modal"
import ButtonElement from "@/app/(site)/_components/GCMolecules/ButtonElement"
import BasketProductCard from "@/app/(site)/_components/Organisms/Cards/ProductCards/BasketProductCard"
import { useHideGladlyIcon } from "@/app/(site)/_utils/_hooks/useHideGladlyIcon"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { HIT_TARGET_CLASS } from "@/src/utils/a11y/hitTarget"
import { clsx } from "@/src/utils/clsx"
import { t } from "@/translations"

interface OutOfStockModalProps {
    isOpen: boolean
    onClose: () => void
    items: OutOfStockItem[]
    type: ModalState["type"]
    langCode: string
    isCartEmpty?: boolean
    totalRemainingItemsInBasket?: number
}

export default function OutOfStockModal({
    isOpen,
    onClose,
    items,
    type,
    langCode,
    isCartEmpty = false,
    totalRemainingItemsInBasket = 0
}: OutOfStockModalProps) {
    useHideGladlyIcon(isOpen)

    if (!isOpen || items.length === 0) return null

    const getModalTitle = () => {
        if (type === "some_items_unavailable") {
            return t(langCode, "checkout.unavailable_items.out_of_stock.title")
        }

        if (type === "cart_empty") {
            return t(langCode, "checkout.unavailable_items.cart_empty.title")
        }
        return ""
    }

    const getModalMessage = () => {
        if (type === "some_items_unavailable") {
            return t(langCode, "checkout.unavailable_items.out_of_stock.message")
        }

        if (type === "cart_empty") {
            return t(langCode, "checkout.unavailable_items.cart_empty.message")
        }

        return ""
    }

    return (
        <Modal open={isOpen} onOpenChange={onClose}>
            <Modal.Content
                className="!md:p-16 flex max-h-[378px] flex-col !p-16 lg:max-h-[668px]"
                aria-describedby="out of stock modal"
            >
                <Modal.Title className="flex items-center justify-between pb-24 Trim_Mono_POC_Text_15_130_7_500_uppercase">
                    {getModalTitle()}
                    <Modal.Close
                        className={clsx("p-5", FOCUS_VISIBLE_CLASS, HIT_TARGET_CLASS)}
                    >
                        <IconClose className="h-10 w-10" />
                    </Modal.Close>
                </Modal.Title>
                {(() => {
                    const totalRemoved = (items || []).reduce(
                        (sum, item) => sum + (item.unavailable || 0),
                        0
                    )
                    if (totalRemoved > 0) {
                        return (
                            <div className="text-col-text-alert Trade_Gothic_Text_14_150_0_700">
                                {t(langCode, "checkout.unavailable_items.item_removed", {
                                    count: totalRemoved
                                })}
                            </div>
                        )
                    }
                    return null
                })()}
                <p className="pb-16 Trade_Gothic_Text_14_150_0_300">
                    {getModalMessage()}
                </p>
                <div className="flex h-auto flex-col overflow-y-auto overflow-x-hidden thin-scrollbar">
                    {Array.isArray(items) &&
                        items.map((unavailableItem: OutOfStockItem) => {
                            const findSize = Array.isArray(unavailableItem.product.items)
                                ? unavailableItem.product.items.find(
                                      (productItem: any) =>
                                          unavailableItem.item === productItem.item
                                  )
                                : null
                            return (
                                <BasketProductCard
                                    key={unavailableItem.item}
                                    product={unavailableItem.product}
                                    lineId=""
                                    itemId=""
                                    quantity={0}
                                    oneProduct={true}
                                    totalPriceBeforeDiscount={
                                        unavailableItem.product.priceBeforeDiscount || ""
                                    }
                                    totalPriceAfterDiscount={
                                        unavailableItem.product.price || ""
                                    }
                                    hasDiscount={false}
                                    size={findSize?.name || ""}
                                    langCode={langCode}
                                    pfasRestricted={false}
                                    isQuantityLoading={false}
                                    isVoucherLoading={false}
                                    handleQuantityChange={() => {}}
                                    hideBasketFunctionality={true}
                                />
                            )
                        })}
                    {(() => {
                        if (totalRemainingItemsInBasket > 0 && !isCartEmpty) {
                            return (
                                <div className="pb-8 text-col-text-primary Trade_Gothic_Text_14_150_0_700">
                                    {t(
                                        langCode,
                                        "checkout.unavailable_items.items_remaining",
                                        {
                                            count: totalRemainingItemsInBasket
                                        }
                                    )}
                                </div>
                            )
                        }
                        return null
                    })()}
                    <ButtonElement
                        className="w-full justify-center"
                        text={t(langCode, "checkout.unavailable_items.continue_checkout")}
                        size={{ desktop: "small", mobile: "small" }}
                        theme="primary-black"
                        onClick={onClose}
                    />
                </div>
            </Modal.Content>
        </Modal>
    )
}
